/**
 * API client. Access token lives in MEMORY ONLY (spec §10.1) — never localStorage, never a cookie
 * we read, never a URL. It is refreshed through identity-api's httpOnly refresh cookie, which is
 * what makes SSO across the Techedify apps seamless.
 */

/*
 * Fallbacks are prod defaults so a missing VITE_* build var fails toward prod, not localhost.
 * Still meant to be set explicitly per environment.
 *
 * VITE_BOX_API_URL is the ORIGIN ONLY — no `/box`. Every call site already asks for `/box/...`
 * (`api.get('/box/projects/…')`) and wsUrl() appends `/box/ws`, so a base that carried the prefix
 * would send `/box/box/projects/…`: still routed by the ALB's `/box/*` rule, but a 404 at the
 * service, which is the confusing kind of broken.
 *
 * Both services live on the same host and are told apart by path prefix (`/box/*` → box-api,
 * `/auth/*` → identity-api) — that is why the two fallbacks are the same origin. The host is the
 * one the deploy itself verifies: .gitlab-ci.yml checks https://prod-api.techedify.com/box/health.
 */
const API_BASE: string =
  (import.meta.env.VITE_BOX_API_URL as string) ?? 'https://prod-api.techedify.com';
const IDENTITY_BASE: string =
  (import.meta.env.VITE_IDENTITY_API_URL as string) ?? 'https://prod-api.techedify.com';

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

export interface ApiError {
  code: string;
  message: string;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.status = status;
    this.code = error.code;
  }
}

/** Exchange the httpOnly refresh cookie for an access token. Deduped across concurrent callers. */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${IDENTITY_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        // The server actively rejected the refresh cookie — the session really is gone.
        accessToken = null;
        return false;
      }
      const data = (await res.json()) as { access_token?: string };
      if (data.access_token) accessToken = data.access_token;
      return accessToken !== null;
    } catch {
      // Network/unreachable — NOT proof the session is invalid. Keep any token we already hold
      // (e.g. one just handed over by the OAuth callback) instead of signing the user out.
      return accessToken !== null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (res.status === 401 && retry) {
    const refreshed = await refreshSession();
    if (refreshed) return request<T>(path, init, false);
  }

  if (!res.ok) {
    let error: ApiError = { code: 'request_failed', message: res.statusText };
    try {
      const body = (await res.json()) as { error?: ApiError };
      if (body.error) error = body.error;
    } catch {
      /* non-JSON error body — keep the status text */
    }
    throw new ApiRequestError(res.status, error);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export const API_BASE_URL = API_BASE;
export const IDENTITY_BASE_URL = IDENTITY_BASE;
